import { FC, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { urlValidation, integerValidation, minLengthValidation, DEFAULT_NAME_MIN_LENGTH } from 'utils/patterns';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useS3ImageUploader } from 'hooks/useS3ImageUploader';
import { Button } from 'components/Core/Button';
import { Switch } from 'components/Core/Switch/Switch';
import { ImageUploader } from 'components/Core/ImageUploader/ImageUploader';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { IBrandBasicData } from 'stores/brands/types';
import { ReactComponent as CopyIcon } from 'assets/copy_icon.svg';
import { defaultValues } from './defaults';
import { brandBasicFormTranslations } from './translations';
import { BrandBasicFormTransFields, IBrandBasicFormWithFiles } from './types';
import styles from './brandBasicForm.module.scss';

export const BrandBasicForm: FC = observer(() => {
  const {
    brandsStore: { brandBasicData, changeBrand, addBrand, deleteBrand },
    alertStore: { showAlert },
    popUpStore: { openPopUp, closePopUp },
  } = useStore();

  const { t } = useMyTranslations<BrandBasicFormTransFields>([brandBasicFormTranslations.name]);

  const navigate = useNavigate();

  const { mutate, isLoading } = useMutation(brandBasicData ? changeBrand : addBrand, {
    mutationKey: 'addBrand-changeBrand',
    onSuccess: (res: IBrandBasicData | null) => {
      showAlert(t(res ? 'createBrandSuccess' : 'editBrandSuccess'), 'success');
      res && navigate(`/brands/${res.id}`);
    },
  });

  const { mutate: deleteBrandMutation, isLoading: isLoadingDeleting } = useMutation(deleteBrand, {
    mutationKey: 'deleteProduct',
    onSuccess: () => {
      closePopUp();
      showAlert(t('deleteBrandSuccess'), 'success');
      navigate(`/brands`, { replace: false });
    },
  });

  const useFormMethods = useForm<IBrandBasicFormWithFiles>({
    defaultValues: brandBasicData || defaultValues,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useFormMethods;

  const onCopyClick = useCallback(() => {
    const { id } = getValues();
    if (id) {
      navigator.clipboard.writeText(id);
      showAlert('Brand ID copied to clipboard', 'info');
    }
  }, []);

  const { uploadImage, isLoading: isImageUploading } = useS3ImageUploader<IBrandBasicFormWithFiles>({ setValue });

  const onSubmitHandler = useCallback(async (inputData: IBrandBasicFormWithFiles) => {
    const { logoUrlFile: lf, pointerUrlFile: pf } = inputData;
    try {
      if (lf) await uploadImage(lf, 'logoUrl', 'brands');
      if (pf) await uploadImage(pf, 'pointerUrl', 'brands-markers');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { logoUrlFile, pointerUrlFile, ...data } = getValues();
      mutate(data);
    } catch {
      showAlert('Problems with image upload', 'warning');
    }
  }, []);

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <FormProvider {...(useFormMethods as any)}>
      <div className={styles.content}>
        <form className={styles.form}>
          <section className={`${styles.leftSection}`}>
            <div className={`${styles.formSection}`}>
              <h6 className={`${styles.title} ${styles.titleWithMargin}`}>{t('title')}</h6>
              <ControlledInput
                name='id'
                type='text'
                required
                label={t('id')}
                readOnly={!!brandBasicData}
                iconPosition='right'
                icon={
                  <button className={styles.copyBtn} type='button' onClick={onCopyClick}>
                    <CopyIcon width={20} height={20} />
                  </button>
                }
              />
              <ControlledInput
                name='name'
                type='text'
                required
                label={t('name')}
                validation={minLengthValidation(DEFAULT_NAME_MIN_LENGTH)}
              />
              <ControlledInput name='website' type='text' label={t('website')} validation={urlValidation()} />
              <ControlledInput
                name='weight'
                type='number'
                label={t('weight')}
                validation={integerValidation(0, 1000)}
              />
              <div className={styles.checkboxWrap}>
                <h6 className={`${styles.title}`}>{t('status')}</h6>
                <Switch
                  isActive={watch('status') === 'Active'}
                  onChange={() => setValue('status', getValues('status') === 'Active' ? 'Inactive' : 'Active')}
                />
              </div>
            </div>
            <div className={styles.bottomSection}>
              <Button
                title={t('save')}
                onClick={handleSubmit(onSubmitHandler)}
                isDisabled={isImageUploading || isSubmitting || isLoading || isLoadingDeleting}
              />
              {brandBasicData && (
                <Button
                  title={t('delete')}
                  onClick={() =>
                    openPopUp({
                      title: t('confirmationTitle'),
                      description: t('approve-text'),
                      buttonLabel: t('confirm'),
                      formName: 'confirmation',
                      cb: () => deleteBrandMutation(brandBasicData.id),
                    })
                  }
                  isDisabled={isImageUploading || isSubmitting || isLoading || isLoadingDeleting}
                  classNameProp={styles.deleteBtn}
                />
              )}
            </div>
          </section>

          <section className={styles.rightSection}>
            <ImageUploader name='logoUrl' title={t('logoUrl')} required path='brands' />
            <ImageUploader name='pointerUrl' title={t('pointerUrl')} required path='brands-markers' />
          </section>
        </form>
      </div>
    </FormProvider>
  );
});
